'use client';

import { useAccountBalance } from '@svs/nova-core/nextjs/auth';
import { updateCouponWallet } from '@svs/nova-poolbetting/core/frameworks/actions/coupon';
import {
  useCouponId,
  useCouponIndices,
  useCouponType,
  useCouponWallets,
  useHasHydratedCouponStore,
} from '@svs/nova-poolbetting/core/frameworks/nextjs/coupon';
import { getCouponIndex } from '@svs/nova-poolbetting/schema/coupon';
import type { WalletType } from '@svs/nova-poolbetting/schema/frameworks';
import type { ActivatedDrawId } from '@svs/nova-poolbetting/schema/marketplace';
import {
  CouponAdditionsItem,
  CouponAdditionsWalletSelector,
  type MarketplaceWallet,
  type PlayerWallet,
} from '@svs/nova-poolbetting/ui/CouponAdditions';
import { Skeleton } from '@svs/ui/react/skeleton';
import { useRouter, useSearchParams } from 'next/navigation';
import { type ReactNode, useEffect } from 'react';

import { getWalletsData, type InjectedWalletProps, withContextWallet } from '@/components/index.browser';

type WalletSelectorProps = {
  wallets: (PlayerWallet | MarketplaceWallet)[];
  activatedDrawId?: ActivatedDrawId;
  activatedDrawIdFromUrl?: ActivatedDrawId;
};

type WalletSelectorPropsWithInjectedWallet = WalletSelectorProps & InjectedWalletProps;

type DisabledWallet = {
  id: ActivatedDrawId;
  text: string;
};

/**
 * Component responsible for rendering the wallet selector container on the client side.
 *
 * @param root0 props
 * @param root0.wallets list of wallets to select from
 * @param root0.activatedDrawId activated draw id selected
 * @param root0.contextWallets wallets from context, used to determine what's visible to the user and to get the most up to date wallet data for the user
 * @param root0.activatedDrawIdFromUrl activated draw id from url, used to determine if we need to update the coupon wallet on load
 * @returns renders the wallet selector container
 */
// eslint-disable-next-line complexity
export function WalletSelectorContainerClient({
  wallets,
  activatedDrawId,
  contextWallets,
  activatedDrawIdFromUrl,
}: WalletSelectorPropsWithInjectedWallet): ReactNode {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [couponId, couponIndex] = useCouponId();
  const couponType = useCouponType({ couponId, couponIndex });
  const isSingleRows = couponType === 'singlerows';
  const couponIndices = useCouponIndices({ couponId });
  const activeWallets = useCouponWallets({ couponId });
  const hasHydratedCouponStore = useHasHydratedCouponStore();
  const accountBalance = useAccountBalance();
  const walletsToUse = contextWallets === undefined ? wallets : getWalletsData(accountBalance.balance, contextWallets);

  const disabledWallets: DisabledWallet[] = [];
  if (activeWallets && !isSingleRows) {
    const filteredActiveWallets = Object.keys(activeWallets).filter((key) => key !== `${couponIndex}`);
    for (const key of filteredActiveWallets) {
      const keyCouponIndex = Number.parseInt(key, 10);
      if (!getCouponIndex(keyCouponIndex)) {
        continue;
      }
      const value = activeWallets[keyCouponIndex];
      if (value && typeof value === 'number' && keyCouponIndex !== couponIndex) {
        disabledWallets.push({ id: value, text: `Kupong ${keyCouponIndex + 1}` });
      }
    }
  }

  const handleValueChange = (value: ActivatedDrawId | WalletType): void => {
    void updateCouponWallet(value, { couponId, couponIndices: isSingleRows ? couponIndices : [couponIndex] });
    const qs = new URLSearchParams(searchParams.toString());
    const activatedDrawIdQs = qs.get('activatedDrawId');
    if (value === 'WALLET_PLAYER' && activatedDrawIdQs) {
      qs.delete('activatedDrawId');
      router.replace(`?${qs.toString()}`, { scroll: false });
    }
    if (typeof value === 'number') {
      qs.set('activatedDrawId', `${value}`);
      router.replace(`?${qs.toString()}`, { scroll: false });
    }
  };

  const shouldUpdateCouponWallet =
    activatedDrawIdFromUrl &&
    hasHydratedCouponStore &&
    !disabledWallets.some((wallet) => wallet.id === activatedDrawIdFromUrl);

  const activeWallet: WalletType | ActivatedDrawId | undefined = hasHydratedCouponStore
    ? activeWallets?.[couponIndex]
    : undefined;

  useEffect(() => {
    if (
      shouldUpdateCouponWallet &&
      !activeWallet &&
      walletsToUse.some(
        (wallet) => wallet.type === 'WALLET_MARKETPLACE_PLAYER' && wallet.activatedDrawId === activatedDrawIdFromUrl
      )
    ) {
      void updateCouponWallet(activatedDrawIdFromUrl, {
        couponId,
        couponIndices: isSingleRows ? couponIndices : [couponIndex],
      });
    }
  }, [
    activatedDrawIdFromUrl,
    couponId,
    couponIndex,
    couponIndices,
    shouldUpdateCouponWallet,
    isSingleRows,
    activeWallet,
    walletsToUse,
  ]);

  if (walletsToUse.length === 0) {
    return undefined;
  }

  return (
    <CouponAdditionsItem
      title="Välj spelkassa"
      description="Vill du spela med ditt eget spelkonto eller ett lags spelkonto?"
    >
      {hasHydratedCouponStore && (
        <CouponAdditionsWalletSelector
          wallets={walletsToUse}
          activatedDrawId={activatedDrawId}
          onValueChange={handleValueChange}
          disabledWallets={disabledWallets}
          selectedCouponWallet={activeWallet}
        />
      )}
      {!hasHydratedCouponStore && <Skeleton className="h-10 w-full" />}
    </CouponAdditionsItem>
  );
}

export default withContextWallet<WalletSelectorProps>(WalletSelectorContainerClient);
